// src/components/ChatPage.js
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Snackbar } from '@mui/material';
import usePreferences from '../contexts/PreferencesContext.js';

import Header         from './Header.js';
import Sidebar        from './Sidebar.js';
import MainPane       from './MainPane.js';
import SettingsDialog from './SettingsDialog.js';
import Onboarding     from './Onboarding.js';

import useChat           from '../hooks/useChat.js';
import useIframeComments from '../hooks/useIframeComments.js';
import useIsOwner        from '../hooks/useIsOwner.js';
import { useRealtime }   from '../contexts/RealtimeContext.js';

import ProductHuntBanner from './ProductHuntBanner.js';

export default function ChatPage() {
  // ─── 1) “top‐level” hooks: must all be called before any conditional return ───

  // 1.1) Preferences, ownership, and socket
  const { model: selectedModel } = usePreferences();
  const isProd                   = process.env.NODE_ENV === 'production';
  const isOwner                  = useIsOwner();
  const socket                   = useRealtime();

  // 1.2) Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { username, chatId, version } = useParams();

  // 1.3) Derived values
  const prototypeId = chatId;
  const versionParam = version != null ? parseInt(version, 10) : null;
  const myId        = username;
  const isFirstChat = !chatId;
  const sessionRoom = `${username}/${chatId}`;

  // 1.4) “fullpageMode” query‐string detection
  const query     = new URLSearchParams(location.search);
  const isFullpage = query.get('fullpageMode') === 'true';

  // 1.5) Local state
  const [sidebarVisible, setSidebarVisible] = useState(
    () => location.state?.sidebarVisible ?? true
  );
  const [creating, setCreating] = useState(false);
  const [firstMessageGenerating, setFirstMessageGenerating] = useState(false);
  const [sessionEndedOpen, setSessionEndedOpen] = useState(false);
  const [pendingFirst, setPendingFirst] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // 1.6) Chat logic (always call useChat unconditionally)
  const chatHook = useChat(isOwner);
  const {
    uploadedImage,
    setUploadedImage,
    messages,
    input,
    setInput,
    sendMessage,
    chatEndRef,
    viewMode,
    setViewMode,
    toggleView,
    history = [],
    timestamps = [],
    historyIndex = 0,
    jumpToVersion = () => {},
    isController,
    takeControl,
    controllerId,
    lastHtml,
    lastCss,
    lastJs,
    canvasContent,
    loading,
    iframeRef,
    onIframeLoad,
    comments,
    commentInput,
    setCommentInput,
    onCommentSubmit,
    collabSessionActive,
    setCollabSessionActive,
    collabSessionElapsed,
    showParentHistory,
    parentMessages,
    loadingParentHistory,
    loadParentHistory,
    parentChatId,
    parentUsername,
    setShowParentHistory,
    optionState,
    jumpToOption,
    setAcceptedId,
    appendOptionAsVersion,
    awaitingOption,
  } = chatHook;

  // 1.7) “handleFirst” callback (always defined unconditionally)
  const handleFirst = useCallback(
    async (text, file) => {
      setCreating(true);
      try {
        // 1) create the chat
        const res = await fetch('/api/chats', { method: 'POST' });
        const { chatId: newId } = await res.json();
        if (!newId) throw new Error('no chatId');

        // 2) navigate into it immediately (drop spinner, set “first generating”)
        setCreating(false);
        setFirstMessageGenerating(true);

        // 3) persist the user message server‐side
        await fetch(`/api/chats/${newId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sender: 'user', content: text }),
        });

        // 4) navigate so that useParams().chatId === newId
        navigate(`/${username}/${newId}`, {
          replace: true,
          state: {
            sidebarVisible: true,
            initialPrompt: text,
            initialFile: file,
          },
        });
      } catch (err) {
        console.error(err);
        alert('Oops, couldn’t create your prototype.');
      } finally {
        setCreating(false);
      }
    },
    [username, navigate]
  );

  // 1.8) All useEffect hooks (must remain at top level, not inside any if)
  useEffect(() => {
    // If we arrived with an initialPrompt, fire sendMessage just once
    if (!chatId || !location.state?.initialPrompt) return;

    const { initialPrompt, initialFile } = location.state;
    sendMessage(initialPrompt, initialFile, { skipUserPersist: true }).finally(
      () => {
        // Clear navigation state so it only triggers once
        navigate(location.pathname, {
          replace: true,
          state: { sidebarVisible },
        });
      }
    );
  }, [
    chatId,
    location.state,
    sendMessage,
    navigate,
    location.pathname,
    sidebarVisible,
  ]);

  useEffect(() => {
    // Redirect to the true owner’s URL if someone else typed another username
    if (!chatId) return;
    (async () => {
      try {
        const res = await fetch(`/api/chats/${chatId}`);
        if (!res.ok) return;
        const meta = await res.json();
        if (
          meta.owner_username &&
          meta.owner_username !== username
        ) {
          const base = `/${meta.owner_username}/${chatId}`;
          const suffix = version ? `/${version}` : '';
          navigate(base + suffix, { replace: true });
        }
      } catch (err) {
        console.error('Owner check failed', err);
      }
    })();
  }, [chatId, username, version, navigate]);

  useEffect(() => {
    // Show “session ended” snackbar if location.state.sessionEnded is true
    if (location.state?.sessionEnded) {
      setSessionEndedOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    // If the host ends the session, remotes get kicked out
    if (isController) return;

    function handleSessionEnd({ room }) {
      if (room !== sessionRoom) return;
      if (!isController) {
        setCollabSessionActive(false);
        setSessionEndedOpen(true);
        navigate(`/${username}/${chatId}`, {
          replace: true,
          state: { sessionEnded: true },
        });
      }
    }

    socket.on('session_end', handleSessionEnd);
    return () => {
      socket.off('session_end', handleSessionEnd);
    };
  }, [
    socket,
    sessionRoom,
    isController,
    setCollabSessionActive,
    navigate,
    username,
    chatId,
  ]);

  useEffect(() => {
    // If navigation came with initialViewMode, apply it once
    if (location.state?.initialViewMode) {
      setViewMode(location.state.initialViewMode);
    }
  }, [location.state, setViewMode]);

  useEffect(() => {
    // Detect “/s/…” URL and auto‐start collaboration session
    if (location.pathname.startsWith('/s/')) {
      setCollabSessionActive(true);
    }
  }, [location.pathname, setCollabSessionActive]);

  useEffect(() => {
    // If navigation came with { sidebarVisible: true }, open it once
    if (location.state?.sidebarVisible) {
      setSidebarVisible(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    // Once pendingFirst+selectedModel exist, fire handleFirst
    if (pendingFirst && selectedModel) {
      const { text, file } = pendingFirst;
      setPendingFirst(null);
      handleFirst(text, file);
    }
  }, [pendingFirst, selectedModel, handleFirst]);

  useEffect(() => {
    // If versionParam is valid and history exists, jump to it
    if (versionParam != null && !Number.isNaN(versionParam)) {
      if (history.length > 0 && versionParam < history.length) {
        jumpToVersion(versionParam);
      }
    }
  }, [versionParam, history.length, jumpToVersion]);

  // 1.9) Inject scrolling/cursor relay into iframe via a custom hook 
  useIframeComments(viewMode, iframeRef, setCommentInput);

  // ─── 2) NOW that all hooks are set up, do the “fullpage” early return ───
  if (isFullpage) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100vw',
          height: '100vh',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <MainPane
          viewMode={viewMode}
          lastHtml={lastHtml}
          lastCss={lastCss}
          lastJs={lastJs}
          canvasContent={canvasContent}
          loading={loading}
          iframeRef={iframeRef}
          onIframeLoad={onIframeLoad}
          comments={comments}
          commentInput={commentInput}
          onCommentSubmit={onCommentSubmit}
          prototypeId={prototypeId}
          myId={myId}
          isController={isController}
          controllerId={controllerId}
          collabSessionActive={collabSessionActive}
        />
      </div>
    );
  }

  // ─── 3) Otherwise, render the normal “ChatPage” UI ───

  // 3.1) Show a loading spinner + header if we’re creating a new chat
  if (creating) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        <Header
          history={[]}
          timestamps={[]}
          historyIndex={0}
          jumpToVersion={() => {}}
        />
        <div style={{ marginTop: 100, fontSize: 24, color: '#888' }}>
          Generating your prototype…
        </div>
      </div>
    );
  }

  // 3.2) If it’s the very first chat, show onboarding
  if (isFirstChat) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Header
          history={[]}
          timestamps={[]}
          historyIndex={0}
          jumpToVersion={() => {}}
        />
        <Onboarding onStart={handleFirst} />
      </div>
    );
  }

  // 3.3) Otherwise, render Header + optional Sidebar + MainPane
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Header
          viewMode={viewMode}
          toggleView={toggleView}
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          history={history}
          timestamps={timestamps}
          historyIndex={historyIndex}
          jumpToVersion={jumpToVersion}
          optionState={optionState}
          jumpToOption={jumpToOption}
          onSettingsClick={() => setSettingsOpen(true)}
          isOwner={isOwner}
          canvasContent={canvasContent}
          isController={isController}
          controllerId={controllerId}
          takeControl={takeControl}
          collabSessionActive={collabSessionActive}
          setCollabSessionActive={setCollabSessionActive}
          collabSessionElapsed={collabSessionElapsed}
        />

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {sidebarVisible && (
          <Sidebar
            messages={
              firstMessageGenerating &&
              messages &&
              messages.length === 1 &&
              messages[0].sender === 'user' &&
              !messages.find((m) => m.sender === 'ai')
                ? [
                    ...messages,
                    {
                      key: 'ai-generating',
                      sender: 'ai',
                      text: 'Generating…',
                      timestamp: new Date().toISOString(),
                    },
                  ]
                : messages
            }
            uploadedImage={uploadedImage}
            setUploadedImage={setUploadedImage}
            chatEndRef={chatEndRef}
            input={input}
            setInput={setInput}
            sendMessage={sendMessage}
            isController={isController}
            collabSessionActive={collabSessionActive}
            showParentHistory={showParentHistory}
            parentMessages={parentMessages}
            loadingParentHistory={loadingParentHistory}
            loadParentHistory={loadParentHistory}
            parentChatId={parentChatId}
            parentUsername={parentUsername}
            setShowParentHistory={setShowParentHistory}
            isOwner={isOwner}
            optionState={optionState}
            jumpToOption={jumpToOption}
            setAcceptedId={setAcceptedId}
            appendOptionAsVersion={appendOptionAsVersion}
            sending={loading}
            awaitingOption={awaitingOption}
          />
          )}

          <MainPane
            viewMode={viewMode}
            lastHtml={lastHtml}
            lastCss={lastCss}
            lastJs={lastJs}
            canvasContent={canvasContent}
            loading={loading}
            iframeRef={iframeRef}
            onIframeLoad={onIframeLoad}
            comments={comments}
            commentInput={commentInput}
            onCommentSubmit={onCommentSubmit}
            prototypeId={prototypeId}
            myId={myId}
            isController={isController}
            controllerId={controllerId}
            collabSessionActive={collabSessionActive}
          />
        </div>
      </div>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* non-blocking “session ended” notification */}
      <Snackbar
        open={sessionEndedOpen}
        autoHideDuration={5000}
        onClose={() => {
          setSessionEndedOpen(false);
        }}
        message="Collaboration Session was ended by the Host"
      />
    </>
  );
}
